<template>
    <section class="enhanced-skills" aria-labelledby="skills-heading">
        <div class="section-header">
            <h3 id="skills-heading" class="section-title">What I'm doing</h3>
            <div class="section-divider">
                <span class="divider-dot"></span>
                <span class="divider-line"></span>
                <span class="divider-dot"></span>
            </div>
            <p class="section-description">Professional services and expertise I bring to every project</p>
        </div>
        
        <div class="skills-grid">
            <div v-for="(service, index) in services" :key="index" 
                 class="skill-card" 
                 :class="getSkillCardClass(service.type)"
                 @mouseenter="handleCardHover(index)"
                 @mouseleave="handleCardLeave()">
                
                <div class="card-background">
                    <div class="card-glow"></div>
                </div>
                
                <div class="skill-icon">
                    <div class="icon-wrapper">
                        <img :src="getSkillIcon(service.type)" 
                             :alt="service.type + ' icon'" 
                             class="skill-image">
                        <div class="icon-pulse"></div>
                    </div>
                </div>
                
                <div class="skill-content">
                    <h4 class="skill-title">{{ service.type }}</h4>
                    <p class="skill-description">{{ service.description }}</p>
                    
                    <div class="skill-features">
                        <div class="feature-tags">
                            <span v-for="tag in getSkillTags(service.type)" 
                                  :key="tag" 
                                  class="feature-tag">
                                {{ tag }}
                            </span>
                        </div>
                    </div>
                </div>
                
                <div class="card-number">{{ String(index + 1).padStart(2, '0') }}</div>
            </div>
        </div>
        
        <!-- Floating particles for visual appeal -->
        <div class="floating-particles">
            <div v-for="i in 6" :key="i" class="particle" :style="getParticleStyle(i)"></div>
        </div>
    </section>
</template>

<script>
export default {
    name: 'EnhancedSkills',
    props: {
        services: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            hoveredCard: null
        };
    },
    methods: {
        getSkillCardClass(type) {
            const classMap = {
                'Web development': 'web-dev-card',
                'Mobile apps development': 'mobile-dev-card',
                'DevOps & Deployment': 'devops-card',
                'API and Service Integration': 'api-dev-card'
            };
            return classMap[type] || 'default-card';
        },
        
        getSkillIcon(type) {
            const iconMap = {
                'Web development': require('@/assets/images/tech/icons8-html-144.png'),
                'Mobile apps development': require('@/assets/images/tech/icons8-flutter-144.png'),
                'DevOps & Deployment': require('@/assets/images/tech/icons8-google-firebase-console-144.png'),
                'API and Service Integration': require('@/assets/images/tech/icons8-api-96.png')
            };
            return iconMap[type] || require('@/assets/icon-dev.svg');
        },
        
        getSkillTags(type) {
            const tagMap = {
                'Web development': ['Vue.js', 'Angular', 'TypeScript', 'HTML5'],
                'Mobile apps development': ['Flutter', 'Dart', 'iOS', 'Android'],
                'DevOps & Deployment': ['Firebase', 'Cloud Services', 'Server Setup', 'Performance Monitor'],
                'API and Service Integration': ['REST', 'Microservices', 'Spring Boot', 'Cloud']
            };
            return tagMap[type] || ['Technology'];
        },
        
        handleCardHover(index) {
            this.hoveredCard = index;
        },
        
        handleCardLeave() {
            this.hoveredCard = null;
        },
        
        getParticleStyle(index) {
            const positions = [
                { top: '10%', left: '5%', animationDelay: '0s' },
                { top: '30%', right: '8%', animationDelay: '2s' },
                { top: '60%', left: '3%', animationDelay: '4s' },
                { top: '80%', right: '15%', animationDelay: '1s' },
                { top: '20%', left: '50%', animationDelay: '3s' },
                { top: '70%', right: '40%', animationDelay: '5s' }
            ];
            return positions[index - 1] || {};
        }
    }
};
</script>

<style scoped>
.enhanced-skills {
    padding: 3rem 0;
    position: relative;
    overflow: hidden;
}

.section-header {
    text-align: center;
    margin-bottom: 3rem;
}

.section-title {
    font-size: 2.5rem;
    font-weight: 700;
    color: var(--white-1);
    margin-bottom: 1rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.section-divider {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 1.5rem 0;
    gap: 1rem;
}

.divider-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.divider-line {
    width: 60px;
    height: 2px;
    background: linear-gradient(90deg, transparent, #667eea, transparent);
}

.section-description {
    color: var(--light-gray-70);
    font-size: 1.1rem;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
}

.skills-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: 2rem;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;
}

.skill-card {
    position: relative;
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 20px;
    padding: 2rem;
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    cursor: pointer;
    overflow: hidden;
}

.skill-card:hover {
    transform: translateY(-10px) scale(1.02);
    border-color: rgba(255, 255, 255, 0.3);
    box-shadow: 
        0 20px 40px rgba(0, 0, 0, 0.3),
        0 0 30px rgba(102, 126, 234, 0.3);
}

.card-background {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
}

.skill-card:hover .card-background {
    opacity: 1;
}

.card-glow {
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(102, 126, 234, 0.1) 0%, transparent 70%);
    animation: rotate 10s linear infinite;
}

.skill-icon {
    display: flex;
    justify-content: center;
    margin-bottom: 1.5rem;
}

.icon-wrapper {
    position: relative;
    width: 80px;
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.skill-image {
    width: 60px;
    height: 60px;
    object-fit: contain;
    filter: drop-shadow(0 4px 12px rgba(102, 126, 234, 0.3));
    transition: transform 0.3s ease;
}

.skill-card:hover .skill-image {
    transform: scale(1.1) rotate(5deg);
}

.icon-pulse {
    position: absolute;
    width: 100%;
    height: 100%;
    border: 2px solid rgba(102, 126, 234, 0.5);
    border-radius: 50%;
    animation: pulse 2s infinite;
    opacity: 0;
}

.skill-card:hover .icon-pulse {
    opacity: 1;
}

.skill-content {
    text-align: center;
}

.skill-title {
    font-size: 1.4rem;
    font-weight: 600;
    color: var(--white-1);
    margin-bottom: 1rem;
    transition: color 0.3s ease;
}

.skill-description {
    color: var(--light-gray-70);
    line-height: 1.6;
    margin-bottom: 1.5rem;
    font-size: 0.95rem;
}

.skill-features {
    margin-bottom: 1.5rem;
}

.feature-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    justify-content: center;
}

.feature-tag {
    background: rgba(102, 126, 234, 0.2);
    color: var(--white-2);
    padding: 0.3rem 0.8rem;
    border-radius: 15px;
    font-size: 0.8rem;
    font-weight: 500;
    border: 1px solid rgba(102, 126, 234, 0.3);
    transition: all 0.3s ease;
}

.skill-card:hover .feature-tag {
    background: rgba(102, 126, 234, 0.3);
    border-color: rgba(102, 126, 234, 0.5);
}

.card-number {
    position: absolute;
    top: 1rem;
    right: 1rem;
    font-size: 2rem;
    font-weight: 800;
    color: rgba(255, 255, 255, 0.1);
    transition: color 0.3s ease;
}

.skill-card:hover .card-number {
    color: rgba(102, 126, 234, 0.3);
}

/* Card specific colors */
.web-dev-card:hover {
    box-shadow: 
        0 20px 40px rgba(0, 0, 0, 0.3),
        0 0 30px rgba(255, 87, 34, 0.3);
}

.mobile-dev-card:hover {
    box-shadow: 
        0 20px 40px rgba(0, 0, 0, 0.3),
        0 0 30px rgba(33, 150, 243, 0.3);
}

.desktop-dev-card:hover {
    box-shadow: 
        0 20px 40px rgba(0, 0, 0, 0.3),
        0 0 30px rgba(156, 39, 176, 0.3);
}

.devops-card:hover {
    box-shadow: 
        0 20px 40px rgba(0, 0, 0, 0.3),
        0 0 30px rgba(255, 193, 7, 0.3);
}

.api-dev-card:hover {
    box-shadow: 
        0 20px 40px rgba(0, 0, 0, 0.3),
        0 0 30px rgba(76, 175, 80, 0.3);
}

/* Floating particles */
.floating-particles {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: -1;
}

.particle {
    position: absolute;
    width: 4px;
    height: 4px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 50%;
    opacity: 0.6;
    animation: float 6s ease-in-out infinite;
}

/* Animations */
@keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

@keyframes pulse {
    0%, 100% {
        transform: scale(1);
        opacity: 0.5;
    }
    50% {
        transform: scale(1.2);
        opacity: 0.8;
    }
}

@keyframes float {
    0%, 100% {
        transform: translateY(0px) rotate(0deg);
    }
    33% {
        transform: translateY(-20px) rotate(120deg);
    }
    66% {
        transform: translateY(10px) rotate(240deg);
    }
}

/* Responsive design */
@media (max-width: 768px) {
    .skills-grid {
        grid-template-columns: 1fr;
        gap: 1.5rem;
        padding: 0 0.5rem;
    }
    
    .skill-card {
        padding: 1.5rem;
    }
    
    .section-title {
        font-size: 2rem;
    }
    
    .skill-title {
        font-size: 1.2rem;
    }
}

@media (max-width: 480px) {
    .enhanced-skills {
        padding: 2rem 0;
    }
    
    .skill-card {
        padding: 1rem;
    }
    
    .section-title {
        font-size: 1.8rem;
    }
}
</style>
